// web-staking-estimator-frontend/src/hooks/useWizardScrollBehavior.js
import { useRef, useEffect } from 'react';

/**
 * Custom hook to manage auto-scroll and focus behavior when wizard sections are revealed.
 * 
 * @param {boolean} hasPin - Whether a map pin has been placed
 * @param {boolean} estimateVisible - Whether the estimate section should be visible
 * @returns {Object} - Refs for request details and estimate sections
 */
export function useWizardScrollBehavior(hasPin, estimateVisible) {
  const reqDetailsRef = useRef(null);
  const estimateRef = useRef(null);
  const revealPrev = useRef({ hasPin: false, estimateVisible: false });
  const pendingEstimateScrollRef = useRef(false);

  // Scroll to Request Details when pin is first placed
  useEffect(() => {
    if (hasPin && !revealPrev.current.hasPin && reqDetailsRef.current) {
      try {
        reqDetailsRef.current.focus();
      } catch {}
      try {
        reqDetailsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } catch {}
    }
    revealPrev.current.hasPin = hasPin;
  }, [hasPin]);

  // Scroll to Estimate when it first becomes visible
  useEffect(() => {
    if (estimateVisible && !revealPrev.current.estimateVisible && estimateRef.current) {
      // If user is typing inside Member Information, defer the scroll until they leave the field
      const ae = document.activeElement;
      const typingInMember =
        ae &&
        (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.tagName === "SELECT") &&
        typeof ae.closest === "function" &&
        !!ae.closest(".memberInfoCard");

      if (typingInMember) {
        pendingEstimateScrollRef.current = true;
      } else {
        try { estimateRef.current.focus(); } catch {}
        try { estimateRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
      }
    }
    revealPrev.current.estimateVisible = estimateVisible;
  }, [estimateVisible]);

  // Handle deferred estimate scroll (when user was typing in member info)
  useEffect(() => {
    function onFocusOut() {
      if (!pendingEstimateScrollRef.current) return;
      if (!estimateVisible || !estimateRef.current) return;
      // Only run once
      pendingEstimateScrollRef.current = false;
      try { estimateRef.current.focus(); } catch {}
      try { estimateRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
    }
    window.addEventListener("focusout", onFocusOut, true);
    return () => window.removeEventListener("focusout", onFocusOut, true);
  }, [estimateVisible]);

  return {
    reqDetailsRef,
    estimateRef,
  };
}
